import React from 'react'
import { explanatoryNoteStyle, labelStyle, multipleSelectBoxStyle } from '@/styles/form'
import { useTranslation } from 'react-i18next'
import { RequiredMark } from '../atoms/required-mark'
import { RadioButton } from '../atoms/radio-button'
import { spacerTopStyle } from '@/styles/common'
import { Input } from '../atoms/input'
import { css } from '@emotion/react'
import { SelectBox } from '../atoms/select-box'
import { isEmployeeByOccupation, isOtherByOccupation, occupationOptions } from '@/utils/form/occupation'
import { AccountInfo } from '@/features/user/account-slice'
import { UseFormReturn } from 'react-hook-form'
import { ErrorMessage } from '../atoms/error-message'
import { RHFSearchAddress } from '../molecules/rhf-search-address'

interface RegisterAccountFormProps {
  useFormReturn: UseFormReturn<AccountInfo>
}

export const RegisterAccountForm: React.FC<RegisterAccountFormProps> = ({ useFormReturn }) => {
  const { t } = useTranslation()

  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useFormReturn

  const watchNationality = watch('nationality')
  const watchGender = watch('gender')
  const watchOccupation = watch('occupation')

  const renderBirthDateError = () => {
    const errorMessage = errors.birthYear?.message || errors.birthMonth?.message || errors.birthDay?.message || errors.birthDate?.message

    if (errorMessage) {
      return <ErrorMessage text={errorMessage} />
    }
  }

  const setAddressValue = (addressText: string) => {
    setValue('address', addressText)
  }

  return (
    <>
      <label css={labelStyle}>
        {t('Residence')}
        <RequiredMark />
      </label>
      <div css={[multipleRadioButtonStyle, multipleSelectBoxStyle]}>
        <RadioButton {...register('nationality')} label={t('Japanese Resident')} checked={watchNationality === 'JPN'} value="JPN" />

        <RadioButton {...register('nationality')} label={t('Overseas Resident')} checked={watchNationality === 'NJP'} value="NJP" />
      </div>

      <>
        <label css={[spacerTopStyle, labelStyle]}>
          {t('Your name')}
          <RequiredMark />
        </label>
        <div css={[multipleSelectBoxStyle]}>
          <Input required {...register('lastName')} placeholder={t('Last name')} error={errors.lastName?.message} />

          <Input required {...register('firstName')} placeholder={t('First name')} error={errors.firstName?.message} />
        </div>
      </>

      {watchNationality === 'JPN' && (
        <>
          <label css={[spacerTopStyle, labelStyle]}>
            {t('Furigana')}
            <RequiredMark />
          </label>
          <div css={[multipleSelectBoxStyle]}>
            <Input
              required
              {...register('familyNameKana', { required: true })}
              placeholder={t('Surname')}
              error={errors.familyNameKana?.message}
            />

            <Input required {...register('givenNameKana')} placeholder={t('May')} error={errors.givenNameKana?.message} />
          </div>
        </>
      )}

      <div css={spacerTopStyle}>
        <label css={labelStyle}>
          {t('Date of birth')}
          <RequiredMark />
        </label>

        <div css={[multipleSelectBoxStyle, birthDateWidth]}>
          <Input {...register('birthYear', { deps: ['birthDay'] })} placeholder="1985" type="number" />
          <Input {...register('birthMonth', { deps: ['birthDay'] })} placeholder="01" type="number" />
          <Input {...register('birthDay')} placeholder="01" type="number" />
        </div>
        {renderBirthDateError()}
      </div>

      <div css={spacerTopStyle}>
        <label css={labelStyle}>
          {t('Gender')}
          <RequiredMark />
        </label>

        <div css={[multipleRadioButtonStyle, multipleSelectBoxStyle]}>
          <RadioButton {...register('gender')} label={t('Male')} checked={watchGender === 'M'} value="M" />
          <RadioButton {...register('gender')} label={t('Female')} checked={watchGender === 'F'} value="F" />
        </div>

        {errors.gender?.message && <ErrorMessage text={errors.gender?.message} />}
      </div>

      <div css={spacerTopStyle}>
        <Input
          required
          {...register('telephone')}
          type="tel"
          label={`${t('Phone number')}(${t('hyphen-less')})`}
          placeholder="0123456789"
          error={errors.telephone?.message}
        />
      </div>

      {watchNationality === 'JPN' && <RHFSearchAddress control={control} name="postalCode" setAddressValue={setAddressValue} />}

      <div css={spacerTopStyle}>
        <Input
          required
          {...register('address')}
          label={t('Address')}
          placeholder={t('Street, City, State, Country')}
          error={errors.address?.message}
        />
      </div>

      <div css={spacerTopStyle}>
        <SelectBox {...register('occupation')} label={t('Occupation')} id="occupation" options={occupationOptions()} />
      </div>

      {isEmployeeByOccupation(watchOccupation) && (
        <div css={spacerTopStyle}>
          <Input {...register('company')} label={t('Company')} placeholder="aipass inc." />
          <p css={explanatoryNoteStyle}>{t('If you would like a receipt with your corporate name, please fill it out')}</p>
        </div>
      )}

      {isOtherByOccupation(watchOccupation) && (
        <div css={spacerTopStyle}>
          <Input {...register('otherOccupation')} label={t('Company')} />
          <p css={explanatoryNoteStyle}>{t('If you would like a receipt with your corporate name, please fill it out')}</p>
        </div>
      )}
    </>
  )
}

const birthDateWidth = css({
  div: {
    width: '30%',
    '&:first-of-type': {
      width: '40%',
    },
  },
})

const multipleRadioButtonStyle = css({
  marginTop: '1rem',
})
